import { Debt } from '../models/Entities/CashFlow/Debt/Debt';
import { Payment } from '../models/Entities/CashFlow/Payment/Payment';
import { CreditorPayment, SettlementDebtSummary } from '../models/Entities/PractitionerAccountStatement/PractitionerAccountStatement';

/**
 * Reemplazos del lado del cliente para cuando los endpoints nuevos del estado de
 * cuenta (contrato §11.1) todavía no están publicados: los servicios los usan
 * al recibir un 404. Sin React ni store.
 */

const round = (value: number): number => Math.round(value * 100) / 100;

const upper = (code: string | null | undefined): string => (code || 'ARS').toUpperCase();

/** El error del API es un 404 (endpoint todavía no publicado o recurso inexistente). */
export const isNotFound = (error: unknown): boolean =>
    typeof error === 'object' && error !== null && (error as { ErrorCode?: number }).ErrorCode === 404;

/** El pago se registró a nombre de otro, aunque cancele deudas del profesional. */
export const isOtherReceiver = (payment: Pick<CreditorPayment, 'receiver' | 'receiverIsCreditor'>, creditorId: string): boolean => {
    if (payment.receiverIsCreditor === true || !payment.receiver) return false;
    return payment.receiver.toLowerCase() !== (creditorId || '').toLowerCase();
};

/**
 * Lo mismo que `debts/creditor/{id}/by-settlement`, armado con las deudas:
 * una fila por (liquidación, moneda). Las borradas no llegan en la lista.
 */
export const groupDebtsBySettlement = (debts: Debt[]): SettlementDebtSummary[] => {
    const groups = new Map<string, SettlementDebtSummary>();
    (debts ?? []).forEach((debt) => {
        const settlementId = debt.settlementId ?? null;
        const currencyCode = upper(debt.currencyCode);
        const key = `${settlementId ?? 'none'}|${currencyCode}`;
        const date = debt.debtDate ? new Date(debt.debtDate) : undefined;
        const current = groups.get(key) ?? {
            settlementId,
            currencyCode,
            originalAmount: 0,
            paidAmount: 0,
            pendingAmount: 0,
            debtCount: 0,
            firstDebtDate: null,
            lastDebtDate: null,
            isSettled: true
        };
        current.originalAmount = round(current.originalAmount + (debt.originalAmount ?? 0));
        current.paidAmount = round(current.paidAmount + (debt.paidAmount ?? 0));
        const pending = debt.isDebtSettled ? 0 : Math.max(0, debt.pendingAmount ?? 0);
        current.pendingAmount = round(current.pendingAmount + pending);
        current.debtCount += 1;
        if (pending > 0) current.isSettled = false;
        if (date && !Number.isNaN(date.getTime())) {
            const first = current.firstDebtDate ? new Date(current.firstDebtDate) : undefined;
            const last = current.lastDebtDate ? new Date(current.lastDebtDate) : undefined;
            if (!first || date < first) current.firstDebtDate = date;
            if (!last || date > last) current.lastDebtDate = date;
        }
        groups.set(key, current);
    });
    return Array.from(groups.values()).sort(
        (a, b) => (b.settlementId ?? -1) - (a.settlementId ?? -1) || a.currencyCode.localeCompare(b.currencyCode)
    );
};

/**
 * Un pago de `payments?payer&receiver` con la forma de `imputed-to-creditor`. Sin
 * el endpoint nuevo no se sabe a qué deudas se imputó: la lista queda vacía y lo
 * imputado es lo que informa el pago.
 */
export const paymentToCreditorPayment = (payment: Payment, creditorId: string): CreditorPayment => ({
    paymentId: payment.id,
    paymentDate: payment.paymentDate,
    payer: payment.payer,
    receiver: payment.receiver,
    receiverIsCreditor: (payment.receiver || '').toLowerCase() === (creditorId || '').toLowerCase(),
    currencyCode: upper(payment.currencyCode),
    amount: payment.amount,
    invoiceAmount: payment.invoiceAmount ?? null,
    retentionAmount: payment.retentionAmount ?? null,
    bankChargesAmount: payment.bankChargesAmount ?? null,
    comments: payment.comments ?? null,
    imputations: [],
    totalImputedToCreditor: payment.imputedAmount ?? payment.amount
});

/** Más nuevos primero, como el endpoint. */
export const sortPaymentsByDateDesc = (payments: CreditorPayment[]): CreditorPayment[] =>
    [...(payments ?? [])].sort((a, b) => new Date(b.paymentDate).getTime() - new Date(a.paymentDate).getTime());
